import { useEffect, useRef, useSyncExternalStore } from "react";
import { isInviteLinkPath } from "../utils/invite-link";
import {
  needsSelectedGroup,
  parseLaunch,
  withoutQuery,
} from "../utils/launch-action";
import { OPEN_URL_MESSAGE } from "../utils/push-routing";

const listeners = new Set();
let pendingLaunch = null;
let initialUrl = window.location.href;

const setPendingLaunch = (launch) => {
  pendingLaunch = launch;
  listeners.forEach((listener) => listener());
};

// Also strips start_url's ?source=pwa: with any query string Framework7's
// router stacks a second home page on top of the first.
const takeLaunchFromLocation = () => {
  const { search } = window.location;
  if (!search) return;
  pendingLaunch = parseLaunch(search);
  window.history.replaceState(
    window.history.state,
    "",
    withoutQuery(window.location),
  );
};

// navigate-existing loads the target URL and then also passes it to the
// consumer; only launches into an already running page need handling here.
const handleLaunchParams = ({ targetURL } = {}) => {
  if (!targetURL) return;
  if (targetURL === initialUrl) {
    initialUrl = null;
    return;
  }
  const url = new URL(targetURL);
  if (isInviteLinkPath(url.pathname)) {
    if (url.pathname !== window.location.pathname) {
      window.location.assign(url.pathname);
    }
    return;
  }
  const launch = parseLaunch(url.search);
  if (launch) setPendingLaunch(launch);
};

const handleServiceWorkerMessage = ({ data } = {}) => {
  if (data?.type !== OPEN_URL_MESSAGE || typeof data.url !== "string") return;
  const launch = parseLaunch(new URL(data.url, window.location.origin).search);
  if (launch) setPendingLaunch(launch);
};

takeLaunchFromLocation();
window.launchQueue?.setConsumer?.(handleLaunchParams);
navigator.serviceWorker?.addEventListener?.(
  "message",
  handleServiceWorkerMessage,
);

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getPendingLaunch = () => pendingLaunch;

export const useLaunchAction = ({ ready, hasGroup, onLaunch }) => {
  const launch = useSyncExternalStore(subscribe, getPendingLaunch);
  const latest = useRef(onLaunch);
  latest.current = onLaunch;

  useEffect(() => {
    if (!launch || !ready || pendingLaunch !== launch) return;
    setPendingLaunch(null);
    if (hasGroup || !needsSelectedGroup(launch)) latest.current(launch);
  }, [launch, ready, hasGroup]);
};
